import { useEffect, useRef } from 'react';
import { useCommentStore } from '@/store/commentStore.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import { useAccountStore } from '@/store/accountStore.ts';
import { getSupabaseSync, getSupabaseForMode } from '@/api/supabaseClient.ts';
import { capabilities } from '@/capabilities.ts';
import {
  addComment as apiAddComment,
  deleteComment as apiDeleteComment,
  setApproval as apiSetApproval,
  getSessionComments,
  getSessionApprovals,
} from '@/api/reviewApi.ts';
import type { ReviewCommentRow, ReviewApprovalRow } from '@/api/reviewApi.ts';
import type { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import type { Comment, Approval } from '@/types/timeline.ts';
import { normalizeApprovalStatus } from '@/types/timeline.ts';
import { uuidToNegId } from '@/previs/commentId.ts';

/** Play a short notification chime via the shared AudioContext (880Hz sine, 0.15s).
 *  Only plays if the AudioContext is running (user has interacted with the page). */
function playChime() {
  import('@/audio/audioProcessor.ts').then(({ getAudioContextIfExists }) => {
    try {
      const ctx = getAudioContextIfExists();
      if (!ctx || ctx.state !== 'running') return; // Respect browser autoplay policy
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.value = 0.08;
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.15);
      osc.onended = () => { osc.disconnect(); gain.disconnect(); };
    } catch {
      // Web Audio not available — ignore
    }
  }).catch(() => {});
}

/** Notify UI of incoming remote comment (visual flash + optional chime) */
function notifyRemoteComment() {
  import('@/store/uiStore.ts').then(({ useUiStore }) => {
    useUiStore.getState().pingRemoteComment();
  });
  playChime();
}

/** Resolve a remote parent UUID to its local numeric ID via the local↔remote map.
 *  Falls back to a deterministic negative ID if the parent isn't in the map yet. */
function resolveLocalParentId(remoteParentId: string | null, localToRemote: Map<number, string>): number | null {
  if (!remoteParentId) return null;
  for (const [localId, remoteId] of localToRemote.entries()) {
    if (remoteId === remoteParentId) return localId;
  }
  return uuidToNegId(remoteParentId);
}

/** Convert a Supabase review_comments row into a local Comment */
function rowToComment(row: ReviewCommentRow, localToRemote: Map<number, string>): Comment {
  return {
    id: uuidToNegId(row.id),
    shotId: row.shot_id,
    timecode: row.timecode,
    author: row.author,
    text: row.text,
    createdAt: row.created_at,
    resolvedAt: row.resolved_at,
    parentCommentId: resolveLocalParentId(row.parent_comment_id, localToRemote),
    annotationSvg: row.annotation_svg ?? null,
    screenplayPage: row.screenplay_page ?? null,
    sceneId: row.scene_id ?? null,
  };
}

/** Convert a Supabase review_approvals row into a local Approval */
function rowToApproval(row: ReviewApprovalRow): Approval {
  return {
    id: uuidToNegId(row.id),
    shotId: row.shot_id,
    status: normalizeApprovalStatus(row.status),
    approvedBy: row.approved_by,
    approvedAt: row.approved_at ?? new Date().toISOString(),
    notes: row.notes,
    revision: row.revision,
  };
}

/**
 * Hook that manages Supabase sync for a review session.
 *
 * - Realtime subscription (postgres_changes) for live updates
 * - On reconnect, does a full fetch to catch anything missed during disconnect
 * - Patches outbound writes to Supabase
 * - Flushes pre-existing local comments on first connect
 */
export function useReviewSync(sessionId: string | null) {
  // In embedded mode, wait for the native access token before subscribing.
  // Without this, the Realtime channel joins with the anon key, RLS silently
  // filters postgres_changes events, and live comments never arrive.
  const nativeToken = useAccountStore(s => s.nativeAccessToken);

  const channelRef = useRef<RealtimeChannel | null>(null);
  const sessionRef = useRef<string | null>(null);

  // Track local IDs we've already sent to Supabase so we can dedup echoes
  const sentLocalIds = useRef(new Set<number>());

  // Map local comment IDs to remote UUIDs for resolve/unresolve/delete
  const localToRemoteId = useRef(new Map<number, string>());

  // Track whether we've flushed pre-existing local comments for this session
  const flushedRef = useRef(false);

  // Track whether we've been connected before (to distinguish reconnect from first connect)
  const wasConnectedRef = useRef(false);

  // Suppress chimes briefly after first subscribe so initial/catch-up events don't beep
  const subscribedAtRef = useRef(0);

  /** Full fetch of comments + approvals — used on reconnect to catch missed events */
  const fetchAll = async (sid: string) => {
    try {
      const [commentRows, approvalRows] = await Promise.all([
        getSessionComments(sid),
        getSessionApprovals(sid),
      ]);
      const cs = useCommentStore.getState();
      const myName = cs.reviewerName;
      for (const row of commentRows) {
        // Our own echo — match by local_id AND author to avoid cross-client collisions
        if (row.local_id && sentLocalIds.current.has(row.local_id) && row.author === myName) {
          localToRemoteId.current.set(row.local_id, row.id);
          continue;
        }
        const comment = rowToComment(row, localToRemoteId.current);
        localToRemoteId.current.set(comment.id, row.id);
        cs._patchRemoteComment(comment);
      }
      for (const row of approvalRows) {
        if (row.local_id && sentLocalIds.current.has(row.local_id) && row.approved_by === myName) continue;
        cs._patchRemoteApproval(rowToApproval(row));
      }
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 403 || status === 410) {
        // Session revoked or expired — invalidate locally
        console.warn('[useReviewSync] Session access lost (expired or revoked)');
        useProjectStore.getState().setSessionId(null);
      } else {
        console.warn('[useReviewSync] Reconnect fetch failed:', err);
      }
    }
  };

  // Realtime subscription
  useEffect(() => {
    if (!sessionId) {
      // Session ended — clean up
      if (channelRef.current) {
        const sb = getSupabaseSync();
        if (sb) sb.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      sessionRef.current = null;
      flushedRef.current = false;
      wasConnectedRef.current = false;
      sentLocalIds.current.clear();
      localToRemoteId.current.clear();
      return;
    }

    // In embedded mode, defer subscription until the native JWT is available.
    // The effect re-fires when nativeToken changes, so we'll subscribe once it arrives.
    if (capabilities.bridge && !nativeToken) {
      console.log('[useReviewSync] Waiting for native access token before subscribing');
      return;
    }

    let cancelled = false;

    const setupChannel = (sb: SupabaseClient) => {
      if (cancelled) return;

      // Session changed — clean up old channel
      if (sessionRef.current !== sessionId && channelRef.current) {
        sb.removeChannel(channelRef.current);
        channelRef.current = null;
        wasConnectedRef.current = false;
      }
      sessionRef.current = sessionId;
      flushedRef.current = false;

      // ── Realtime channel ──────────────────────────────────────────────
      if (!channelRef.current) {
        channelRef.current = sb
          .channel(`review-${sessionId}`)
          .on(
            'postgres_changes',
            { event: 'INSERT', schema: 'public', table: 'review_comments', filter: `session_id=eq.${sessionId}` },
            (payload) => {
              const row = payload.new as ReviewCommentRow;
              const myName = useCommentStore.getState().reviewerName;
              // Our own echo — match by local_id AND author
              if (row.local_id && sentLocalIds.current.has(row.local_id) && row.author === myName) {
                localToRemoteId.current.set(row.local_id, row.id);
                return;
              }
              const comment = rowToComment(row, localToRemoteId.current);
              localToRemoteId.current.set(comment.id, row.id);
              useCommentStore.getState()._patchRemoteComment(comment);
              if (Date.now() - subscribedAtRef.current > 3000) notifyRemoteComment();
            },
          )
          .on(
            'postgres_changes',
            { event: 'DELETE', schema: 'public', table: 'review_comments', filter: `session_id=eq.${sessionId}` },
            (payload) => {
              const old = payload.old as { id: string };
              for (const [localId, remoteId] of localToRemoteId.current.entries()) {
                if (remoteId === old.id) {
                  useCommentStore.getState()._patchRemoteCommentDelete(localId);
                  localToRemoteId.current.delete(localId);
                  break;
                }
              }
            },
          )
          .on(
            'postgres_changes',
            { event: 'INSERT', schema: 'public', table: 'review_approvals', filter: `session_id=eq.${sessionId}` },
            (payload) => {
              const row = payload.new as ReviewApprovalRow;
              const myName = useCommentStore.getState().reviewerName;
              if (row.local_id && sentLocalIds.current.has(row.local_id) && row.approved_by === myName) return;
              useCommentStore.getState()._patchRemoteApproval(rowToApproval(row));
              if (Date.now() - subscribedAtRef.current > 3000) notifyRemoteComment();
            },
          );
      }

      // Pre-set the auth token on the Realtime connection BEFORE subscribing.
      // The supabase-js library has a bug where the join payload is captured in a
      // sendBuffer closure before _onConnOpen can update it via the accessToken
      // callback, so the channel joins without access_token and RLS silently
      // filters all postgres_changes events.
      if (capabilities.bridge && nativeToken) {
        sb.realtime.setAuth(nativeToken);
      }

      channelRef.current.subscribe((status) => {
        console.log(`[useReviewSync] Realtime channel: ${status} (bridge=${capabilities.bridge}, origin=${location.protocol})`);
        if (status === 'SUBSCRIBED') {
          subscribedAtRef.current = Date.now();
          // Fetch on every connect (not just reconnect) to populate
          // localToRemoteId for creator comments from previous sessions.
          // Content dedup in _patchRemoteComment prevents duplicates.
          fetchAll(sessionId);
          wasConnectedRef.current = true;
        }
      });

      // ── Flush pre-existing local comments ─────────────────────────────
      // When a session first connects and there are already local comments
      // (creator was annotating before sharing), push them to Supabase.
      if (!flushedRef.current) {
        flushedRef.current = true;
        // Only flush locally-created comments (positive IDs).
        // Comments loaded from Supabase have negative IDs (uuidToNegId) and already exist in the DB.
        const existing = useCommentStore.getState().comments.filter(c => c.id > 0);
        if (existing.length > 0) {
          for (const c of existing) {
            sentLocalIds.current.add(c.id);
            apiAddComment(sessionId, c.shotId, c.timecode, c.author, c.text, c.id, undefined, c.annotationSvg)
              .then(row => {
                // Map local ID → Supabase UUID so deleteComment can sync
                localToRemoteId.current.set(c.id, row.id);
              })
              .catch(err => console.warn('[useReviewSync] Failed to flush local comment:', err));
          }
        }
        const existingApprovals = useCommentStore.getState().approvals.filter(a => a.id > 0);
        if (existingApprovals.length > 0) {
          for (const a of existingApprovals) {
            sentLocalIds.current.add(a.id);
            apiSetApproval(sessionId, a.shotId, a.status, a.approvedBy, a.id, a.notes, a.revision).catch(
              err => console.warn('[useReviewSync] Failed to flush local approval:', err),
            );
          }
        }
      }
    };

    // Try sync first (instant for web mode), fall back to async for embedded mode
    const sb = getSupabaseSync();
    if (sb) {
      setupChannel(sb);
    } else {
      // Embedded mode: client not ready yet — wait for it
      getSupabaseForMode().then(client => {
        if (client) setupChannel(client);
      });
    }

    return () => { cancelled = true; };
  }, [sessionId, nativeToken]);

  // Patch outbound writes — subscribe to store changes
  useEffect(() => {
    if (!sessionId) return;

    const origAddComment = useCommentStore.getState().addComment;
    const origDeleteComment = useCommentStore.getState().deleteComment;
    const origSetApproval = useCommentStore.getState().setApproval;

    useCommentStore.setState({
      addComment: (shotId, timecode, text, parentId, annotationSvg, screenplayPage, sceneId) => {
        origAddComment(shotId, timecode, text, parentId, annotationSvg, screenplayPage, sceneId);
        const comments = useCommentStore.getState().comments;
        const newest = comments[comments.length - 1];
        if (newest) {
          sentLocalIds.current.add(newest.id);
          const remoteParentId = parentId != null ? localToRemoteId.current.get(parentId) : undefined;
          apiAddComment(sessionId, shotId, timecode, newest.author, text, newest.id, remoteParentId, annotationSvg, screenplayPage, sceneId).catch(
            err => console.warn('Failed to sync comment to Supabase:', err),
          );
        }
      },

      deleteComment: (id) => {
        // Collect all IDs that will be cascade-deleted (parent + children)
        const comments = useCommentStore.getState().comments;
        const idsToDelete = new Set<number>([id]);
        let changed = true;
        while (changed) {
          changed = false;
          for (const c of comments) {
            if (c.parentCommentId !== null && idsToDelete.has(c.parentCommentId) && !idsToDelete.has(c.id)) {
              idsToDelete.add(c.id);
              changed = true;
            }
          }
        }

        origDeleteComment(id);

        // Sync all deleted IDs to Supabase
        for (const localId of idsToDelete) {
          const remoteId = localToRemoteId.current.get(localId);
          if (remoteId) {
            apiDeleteComment(remoteId).catch(
              err => console.warn('Failed to sync delete to Supabase:', err),
            );
            localToRemoteId.current.delete(localId);
          }
        }
      },

      setApproval: (shotId, status, notes) => {
        origSetApproval(shotId, status, notes);
        const approvals = useCommentStore.getState().approvals;
        const newest = approvals[approvals.length - 1];
        if (newest) {
          sentLocalIds.current.add(newest.id);
          apiSetApproval(sessionId, shotId, status, newest.approvedBy, newest.id, notes, newest.revision).catch(
            err => console.warn('Failed to sync approval to Supabase:', err),
          );
        }
      },
    });

    return () => {
      useCommentStore.setState({
        addComment: origAddComment,
        deleteComment: origDeleteComment,
        setApproval: origSetApproval,
      });
    };
  }, [sessionId]);
}
